"use client";

import React, { useEffect, useRef, useState } from "react";
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import * as THREE from "three";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";
import { textureFromCanvas } from "../textures";

gsap.registerPlugin(ScrollTrigger);

const SHOWCASE_DATA = [
  {
    id: "AX-01",
    title: "Robotics",
    tag: "PHYSICAL_INTEL",
    description: "Low-latency execution loops for intelligent systems operating in physical space. Perception, decision, and control processes converge into a continuous real-time compute feedback cycle.",
    modelPath: "/robotarm.glb",
    rotation: [0, -Math.PI / 4, 0],
    scaleMultiplier: 1.0,
    yOffset: 0,
    mobileScale: 0.75,
    mobileYOffset: 1.2,
    metrics: ["TORQUE: 450Nm", "LATENCY: 2ms", "DOF: 06"],
    color: "#06b6d4", // Cyan
    emissiveColor: 0x06b6d4,
    lightColor: 0x06b6d4
  },
  {
    id: "AX-02",
    title: "Cognitive",
    tag: "NEURAL_SYNAPSE",
    description: "Adaptive intelligence workloads executed across high-performance compute. Models learn, refine, and recalibrate continuously through iterative feedback loops and large-scale training cycles.",
    modelPath: "/head.glb",
    rotation: [0, 0, 0],
    scaleMultiplier: 1.0,
    yOffset: 0,
    mobileScale: 0.75,
    mobileYOffset: 1.2,
    metrics: ["SYNAPSE: 1.2T", "LOAD: 88%", "FREQ: 4.2GHz"],
    color: "#d946ef", // Fuchsia
    emissiveColor: 0xd946ef,
    lightColor: 0xd946ef
  },
  {
    id: "AX-03",
    title: "Generation",
    tag: "SPATIAL_GEN",
    description: "On-demand creation across visual, linguistic, and spatial domains. From images and video to large language models and synthetic 3D worlds, intelligence materializes directly from compute.",
    modelPath: "/dragon.glb",
    rotation: [0, 0, 0],
    scaleMultiplier: 3.5,
    yOffset: -2.0,
    mobileScale: 1.9,
    mobileYOffset: -1.8,
    metrics: ["POLY_GEN: 14M", "SEED: 0x8F2A", "VOL: 84%"],
    color: "#8b5cf6", // Violet
    emissiveColor: 0x8b5cf6,
    lightColor: 0x8b5cf6
  }
];

// --- Sub-components for Styling ---

type TerminalBoxProps = {
  children: React.ReactNode;
  tag: string;
  id: string | number;
  color: string;
};

const TerminalBox = ({ children, tag, id, color }: TerminalBoxProps) => (
  <div className="relative p-6 border border-white/10 bg-black/40 backdrop-blur-xl rounded-sm overflow-hidden group max-w-md">
    {/* Corner Accents */}
    <div className="absolute top-0 left-0 h-4 w-4 origin-top-left scale-50 border-l border-t transition-transform duration-300 group-hover:scale-100" style={{ borderColor: color }} />
    <div className="absolute bottom-0 right-0 h-4 w-4 origin-bottom-right scale-50 border-r border-b transition-transform duration-300 group-hover:scale-100" style={{ borderColor: color }} />

    <div className="flex justify-between items-center mb-4 border-b border-white/5 pb-2">
      <span className="font-mono text-[10px] tracking-[0.3em] opacity-50 uppercase">DATA_STREAM_{id}</span>
      <span className="font-mono text-[10px] font-bold" style={{ color }}>{`// ${tag}`}</span>
    </div>
    {children}
    {/* Scanline Effect */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent bg-[length:100%_4px] pointer-events-none animate-pulse" />
    
    {/* Glow effect on hover */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" 
         style={{ boxShadow: `inset 0 0 60px ${color}20` }} />
  </div>
);

const GlitchHeader = ({ text, color }: { text: string; color: string }) => (
  <div className="relative h-20 mb-2">
    <h2 className="absolute inset-0 text-6xl md:text-8xl font-black tracking-tighter text-transparent uppercase opacity-20"
        style={{ WebkitTextStroke: `1px ${color}` }}>{text}</h2>
    <motion.h2 
      animate={{ x: [-1, 1, -1], opacity: [0.8, 1, 0.8] }}
      transition={{ repeat: Infinity, duration: 0.2 }}
      className="absolute inset-0 text-6xl md:text-8xl font-black tracking-tighter uppercase select-none"
      style={{ 
        color: color,
        textShadow: `0 0 40px ${color}80, 0 0 20px ${color}40`
      }}>{text}</motion.h2>
  </div>
);

export default function ThreeModelShowcase() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modelGroupsRef = useRef<THREE.Group[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const dynamicLightRef = useRef<THREE.PointLight | null>(null);
  
  // Pre-generate hex codes after mount to avoid hydration issues and impure render
  const [hexCodes, setHexCodes] = useState<Array<{ code: string; duration: number; delay: number }>>([]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHexCodes(
      Array.from({ length: 30 }).map(() => ({
        code: Math.random().toString(16).slice(2, 8).toUpperCase(),
        duration: 2 + Math.random() * 2,
        delay: Math.random() * 2,
      }))
    );
  }, []);
  
  const backgroundUniformsRef = useRef({
    uTime: { value: 0 },
    uMouse: { value: new THREE.Vector2(0.5, 0.5) },
    uResolution: { value: new THREE.Vector2(1, 1) }
  });
  const codeMaterialRef = useRef<THREE.ShaderMaterial | null>(null);

  // Fix hydration mismatch
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const getSizes = () => ({
        width: containerRef.current?.clientWidth || window.innerWidth,
        height: window.innerHeight
    });
    const sizes = getSizes();

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x010206, 0.055);

    const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
    camera.position.set(0, 0, 15);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true
    });
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(ambientLight);

    // Dynamic colored light that changes with active model
    const dynamicLight = new THREE.PointLight(SHOWCASE_DATA[0].lightColor, 15, 50);
    dynamicLight.position.set(5, 5, 5);
    scene.add(dynamicLight);
    dynamicLightRef.current = dynamicLight;

    // Rim lights
    const rimLight1 = new THREE.SpotLight(0xffffff, 20);
    rimLight1.position.set(-8, 3, 5);
    scene.add(rimLight1);

    const rimLight2 = new THREE.SpotLight(0xffffff, 25);
    rimLight2.position.set(8, -3, 5);
    scene.add(rimLight2);

    // --- BACKGROUND SETUP ---
    const bgGeometry = new THREE.PlaneGeometry(100, 100);
    const bgMaterial = new THREE.ShaderMaterial({
      uniforms: backgroundUniformsRef.current,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec2 uMouse;
        uniform vec2 uResolution;
        varying vec2 vUv;

        void main() {
            vec2 st = vUv;
            st.x *= uResolution.x / uResolution.y;
            vec2 mouse = uMouse;
            mouse.x *= uResolution.x / uResolution.y;

            vec3 color = vec3(0.001, 0.002, 0.006);

            vec2 pos1 = vec2(0.5, 0.5) + vec2(sin(uTime * 0.15) * 0.3, cos(uTime * 0.1) * 0.2);
            pos1.x *= uResolution.x / uResolution.y;
            float dist1 = distance(st, pos1);
            float orb1 = 0.025 * exp(-dist1 * 2.0);
            color += vec3(0.02, 0.05, 0.15) * orb1;

            vec2 pos2 = vec2(0.5, 0.5) + vec2(cos(uTime * 0.1) * 0.4, sin(uTime * 0.2) * 0.3);
            pos2.x *= uResolution.x / uResolution.y;
            float dist2 = distance(st, pos2);
            float orb2 = 0.02 * exp(-dist2 * 2.5);
            color += vec3(0.01, 0.08, 0.2) * orb2;

            float vignette = 1.0 - smoothstep(0.4, 1.4, length(vUv - 0.5) * 1.5);
            color *= vignette;

            gl_FragColor = vec4(color, 1.0);
        }
      `,
      depthWrite: false,
    });
    const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
    bgMesh.position.z = -10;
    bgMesh.renderOrder = -999;
    scene.add(bgMesh);

    // --- CODE PARTICLES SETUP (with dynamic colors) ---
    const chars = [
      '0','1','2','3','4','5','6','7','8','9',
      'A','B','C','D','E','F','X','[',']','<','>',':','/',
      'G','P','U','S','Y','N','C','K','R','L','T','V','M','E','x'
    ];
    const charSize = 128;
    const atlasCanvas = document.createElement('canvas');
    atlasCanvas.width = charSize * chars.length;
    atlasCanvas.height = charSize;
    const atlasCtx = atlasCanvas.getContext('2d', { willReadFrequently: true });

    if (atlasCtx) {
      atlasCtx.clearRect(0,0, atlasCanvas.width, atlasCanvas.height);
      atlasCtx.font = 'bold 75px monospace';
      atlasCtx.textAlign = 'center';
      atlasCtx.textBaseline = 'middle';
      atlasCtx.fillStyle = '#ffffff';
      chars.forEach((char, i) => {
        atlasCtx.fillText(char, (i * charSize) + (charSize / 2), charSize / 2);
      });
    }
    const codeAtlas = textureFromCanvas(atlasCanvas, atlasCtx);

    const particleCount = 80;
    const codeGeometry = new THREE.BufferGeometry();
    const codePositions = new Float32Array(particleCount * 3);
    const codeRandoms = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      codePositions[i * 3] = (Math.random() - 0.5) * 20;
      codePositions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      codePositions[i * 3 + 2] = (Math.random() - 0.5) * 12;
      codeRandoms[i] = Math.random();
    }

    codeGeometry.setAttribute('position', new THREE.BufferAttribute(codePositions, 3));
    codeGeometry.setAttribute('aRandom', new THREE.BufferAttribute(codeRandoms, 1));

    const codeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uTexture: { value: codeAtlas },
        uAtlasSize: { value: chars.length },
        uStringLen: { value: 10.0 },
        uOpacity: { value: 0.12 },
        uSize: { value: 800.0 },
        uColorTint: { value: new THREE.Color(SHOWCASE_DATA[0].emissiveColor) }
      },
      vertexShader: `
        attribute float aRandom;
        varying float vRandom;
        uniform float uSize;
        void main() {
            vRandom = aRandom;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = uSize / -mvPosition.z;
            gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform float uTime;
        uniform float uAtlasSize;
        uniform float uStringLen;
        uniform float uOpacity;
        uniform vec3 uColorTint;
        varying float vRandom;

        void main() {
            float yMin = 0.42;
            float yMax = 0.58;
            if (gl_PointCoord.y < yMin || gl_PointCoord.y > yMax) discard;

            float charIndex = floor(gl_PointCoord.x * uStringLen);
            float localUvX = fract(gl_PointCoord.x * uStringLen);
            // gl_PointCoord runs top-down and so does the atlas buffer - no flip.
            float localUvY = (gl_PointCoord.y - yMin) / (yMax - yMin);

            float changeSpeed = 4.0;
            float uniqueSeed = vRandom + (charIndex * 0.13);
            float timeStep = floor(uTime * changeSpeed + uniqueSeed * 100.0);
            float charFrame = floor(mod(timeStep * uniqueSeed * 10.0, uAtlasSize));

            vec2 atlasUV = vec2((charFrame + localUvX) / uAtlasSize, localUvY);
            vec4 texColor = texture2D(uTexture, atlasUV);
            
            float edgeFade = 1.0;
            if (charIndex < 1.0 || charIndex > uStringLen - 2.0) edgeFade = 0.5;

            vec3 finalColor = uColorTint;
            gl_FragColor = vec4(finalColor * texColor.rgb, texColor.a * uOpacity * edgeFade);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    codeMaterialRef.current = codeMaterial;
    const codeSystem = new THREE.Points(codeGeometry, codeMaterial);
    scene.add(codeSystem);

    // Post-processing with stronger bloom
    const composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(sizes.width, sizes.height),
      1.8, // Stronger bloom
      0.6, 
      0.2
    );
    composer.addPass(bloomPass);

    // Initialize model groups
    modelGroupsRef.current = [];
    SHOWCASE_DATA.forEach(() => {
        const group = new THREE.Group();
        group.visible = false;
        scene.add(group);
        modelGroupsRef.current.push(group);
    });

    // Set Initial Positions
    modelGroupsRef.current[0].position.set(0, 0, 0);
    modelGroupsRef.current[0].scale.set(1, 1, 1);
    modelGroupsRef.current[0].visible = true;
    
    modelGroupsRef.current[1].position.x = -12;
    modelGroupsRef.current[2].position.x = 10;

    const fitModelToView = (model: THREE.Object3D) => {
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const targetSize = 6.0;
        const scaleFactor = targetSize / maxDim;
        if (isFinite(scaleFactor)) {
            model.scale.setScalar(scaleFactor);
            model.position.set(-center.x * scaleFactor, -center.y * scaleFactor, -center.z * scaleFactor);
        }
    };

    // Load models with dynamic colors
    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    const mixers: THREE.AnimationMixer[] = [];
    const isMobile = window.innerWidth < 768;

    dracoLoader.setDecoderPath('/draco/');
    loader.setDRACOLoader(dracoLoader);

    SHOWCASE_DATA.forEach((data, index) => {
      loader.load(data.modelPath, (gltf) => {
        const model = gltf.scene;
        
        if (gltf.animations && gltf.animations.length > 0) {
          const mixer = new THREE.AnimationMixer(model);
          gltf.animations.forEach((clip) => {
            const action = mixer.clipAction(clip);
            action.play();
          });
          mixers.push(mixer);
        }
        
        model.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            const isSkinned = (mesh as THREE.SkinnedMesh).isSkinnedMesh;

            // Use the model's specific color
            const modelColor = data.emissiveColor;
            const wireframeOpacity = isSkinned ? 0.6 : 0.3;

            mesh.material = new THREE.MeshStandardMaterial({
              color: modelColor,
              wireframe: true,
              transparent: true,
              opacity: wireframeOpacity,
              emissive: modelColor,
              emissiveIntensity: 0.6,
              metalness: 0.8,
              roughness: 0.2
            });

            if (isSkinned) (mesh.material as THREE.MeshStandardMaterial & { skinning?: boolean }).skinning = true;

            if (!isSkinned) {
                const edges = new THREE.EdgesGeometry(mesh.geometry, 15);
                const edgeMaterial = new THREE.LineBasicMaterial({
                    color: modelColor,
                    transparent: true,
                    opacity: 0.8,
                    blending: THREE.AdditiveBlending,
                    depthWrite: false
                });
                const highlightWireframe = new THREE.LineSegments(edges, edgeMaterial);
                mesh.add(highlightWireframe);
            }
          }
        });

        fitModelToView(model);
        
        const scaleToUse = (isMobile && data.mobileScale) ? data.mobileScale : data.scaleMultiplier;
        const yOffsetToUse = (isMobile && data.mobileYOffset !== undefined) ? data.mobileYOffset : data.yOffset;
        
        model.scale.multiplyScalar(scaleToUse);
        
        if (yOffsetToUse) {
            model.position.y += yOffsetToUse;
        }
        
        const group = modelGroupsRef.current[index];
        const floatingGroup = new THREE.Group();
        const modelGroup = new THREE.Group();
        
        group.add(floatingGroup);
        floatingGroup.add(modelGroup);
        modelGroup.add(model);
        
        (group as THREE.Group & { floatingGroup: THREE.Group; modelGroup: THREE.Group }).floatingGroup = floatingGroup;
        (group as THREE.Group & { floatingGroup: THREE.Group; modelGroup: THREE.Group }).modelGroup = modelGroup;
        model.rotation.set(data.rotation[0], data.rotation[1], data.rotation[2]);

        ScrollTrigger.refresh();

      }, undefined, (err) => console.error(`Error loading ${data.title}`, err));
    });

    // GSAP Scroll Animation
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
          scrollTrigger: {
              trigger: containerRef.current,
              start: "top top",
              end: "bottom bottom",
              scrub: 1.5,
              onUpdate: (self) => {
                const progress = self.progress;
                const idx = Math.min(Math.floor(progress * SHOWCASE_DATA.length), SHOWCASE_DATA.length - 1);
                setActiveIdx(idx);
                
                // Update dynamic light color
                if (dynamicLightRef.current) {
                  dynamicLightRef.current.color.setHex(SHOWCASE_DATA[idx].lightColor);
                }
                
                // Update code particle color
                if (codeMaterialRef.current) {
                  codeMaterialRef.current.uniforms.uColorTint.value.setHex(SHOWCASE_DATA[idx].emissiveColor);
                }
              }
          }
      });

      const robotScale = isMobile ? SHOWCASE_DATA[0].mobileScale : SHOWCASE_DATA[0].scaleMultiplier;

      // --- MODEL 1 (Robot) ---
      const g1 = modelGroupsRef.current[0];
      tl.set(g1.scale, { x: robotScale, y: robotScale, z: robotScale }, 0);
      tl.to(g1.rotation, { y: Math.PI * 0.5, duration: 4, ease: "none" }, 0);
      tl.to(g1.position, { x: -12, duration: 1.5, ease: "power2.in" }, 4);
      tl.set(g1, { visible: false }, 5.5);

      // --- MODEL 2 (Head) ---
      const g2 = modelGroupsRef.current[1];
      tl.set(g2, { visible: true }, 5.0);
      tl.to(g2.position, {
          x: 0,
          y: isMobile ? SHOWCASE_DATA[1].mobileYOffset : 0,
          duration: 1.5,
          ease: "power3.out",
      }, 5.0);
      tl.to(g2.rotation, { y: -Math.PI * 1.1, duration: 9, ease: "none" }, 6.5);
      tl.to(g2.position, { x: 0, duration: 8 }, 6.5);
      tl.to(g2.position, { x: 14, duration: 2, ease: "power2.inOut" }, 14.5);
      tl.set(g2, { visible: false }, 16.5);

      // --- MODEL 3 (Dragon) ---
      const g3 = modelGroupsRef.current[2];
      const dragonY = isMobile ? SHOWCASE_DATA[2].mobileYOffset : 0;
      
      tl.set(g3, { visible: true }, 16);
      tl.fromTo(g3.position,
          { x: 12, y: dragonY },
          { x: 0, y: dragonY, duration: 2, ease: "power2.out" },
          16
      );
      tl.to(g3.rotation, { y: Math.PI * 0.35, duration: 12, ease: "none" }, 16);
      
      const exitTime = 28;
      tl.to(g3.position, { y: 15, duration: 2.5, ease: "power2.in" }, exitTime);
      tl.to(g3.scale, { x: 0.1, y: 0.1, z: 0.1, duration: 2.5 }, exitTime);
      tl.to(g3.rotation, { x: -0.5, duration: 2.5 }, exitTime);

    }, containerRef);
  
    const clock = new THREE.Clock();
    
    const onMouseMove = (event: MouseEvent) => {
        backgroundUniformsRef.current.uMouse.value.x = event.clientX / window.innerWidth;
        backgroundUniformsRef.current.uMouse.value.y = 1.0 - (event.clientY / window.innerHeight);
    };
    window.addEventListener("mousemove", onMouseMove);

    const resizeRendererToDisplaySize = (renderer: THREE.WebGLRenderer) => {
      const canvas = renderer.domElement;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const pixelRatio = Math.min(window.devicePixelRatio, 2);
      
      const needResize = canvas.width !== Math.floor(width * pixelRatio) || canvas.height !== Math.floor(height * pixelRatio);
      
      if (needResize) {
        renderer.setSize(width, height, false);
        composer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        backgroundUniformsRef.current.uResolution.value.set(width, height);
      }
      return needResize;
    };

    let animationId: number;
    const animate = () => {
      const delta = clock.getDelta();
      const time = clock.getElapsedTime();
      
      resizeRendererToDisplaySize(renderer);
      
      mixers.forEach(mixer => mixer.update(delta));
      
      backgroundUniformsRef.current.uTime.value = time;
      if (codeMaterialRef.current) codeMaterialRef.current.uniforms.uTime.value = time;
      
      codeSystem.rotation.y = time * 0.05;
      codeSystem.rotation.z = time * 0.02;

      modelGroupsRef.current.forEach((group, i) => {
        const g = group as THREE.Group & { floatingGroup?: THREE.Group };
        if (!g.visible || !g.floatingGroup) return;
        g.floatingGroup.position.y = Math.sin(time * 1.4 + i) * 0.15;
        g.floatingGroup.rotation.z = Math.sin(time * 0.6 + i) * 0.05;
      });
              
      // Animated rim lights
      rimLight1.intensity = 20 + Math.sin(time * 1.5) * 8;
      rimLight2.intensity = 25 + Math.cos(time * 2) * 10;
      
      // Pulse the dynamic light
      if (dynamicLightRef.current) {
        dynamicLightRef.current.intensity = 15 + Math.sin(time * 3) * 5;
      }
      
      composer.render();
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(animationId);
      ctx.revert();
      renderer.dispose();
      bgGeometry.dispose();
      bgMaterial.dispose();
      codeGeometry.dispose();
      codeMaterial.dispose();
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full bg-[#010206]" style={{ height: '300vh' }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Animated hex code background - only render on client to avoid hydration mismatch */}
        {isMounted && hexCodes.length > 0 && (
          <div className="absolute inset-0 z-0 opacity-15 font-mono text-[10px] flex flex-wrap gap-4 p-4 select-none pointer-events-none overflow-hidden"
               style={{ color: SHOWCASE_DATA[activeIdx].color }}>
              {hexCodes.map((hex, i) => (
                <motion.span 
                  key={i}
                  animate={{ opacity: [0.3, 0.8, 0.3] }}
                  transition={{ duration: hex.duration, repeat: Infinity, delay: hex.delay }}
                >
                  0x{hex.code}
                </motion.span>
              ))}
          </div>
        )}

        <canvas ref={canvasRef} className="block w-full h-full" />
        
        {/* Atmosphere layers */}
        <div className="absolute inset-0 pointer-events-none z-20 bg-[radial-gradient(circle_at_center,transparent_20%,#010206_100%)]" />
        <div className="absolute inset-0 pointer-events-none z-20 bg-[url('/noise.svg')] opacity-10 mix-blend-overlay"></div>

        {/* Decorative Corner Framing with color */}
        <motion.div 
          className="absolute top-10 left-10 w-32 h-32 border-l-2 border-t-2 pointer-events-none z-20 transition-colors duration-1000"
          style={{ borderColor: SHOWCASE_DATA[activeIdx].color }}
        />
        <motion.div 
          className="absolute bottom-10 right-10 w-32 h-32 border-r-2 border-b-2 pointer-events-none z-20 transition-colors duration-1000"
          style={{ borderColor: SHOWCASE_DATA[activeIdx].color }}
        />

        {/* Dynamic Content Panel */}
        <div className="absolute inset-0 flex items-center px-8 md:px-24 z-30 pointer-events-none">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIdx}
              initial={{ opacity: 0, x: -50, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 50, scale: 0.95 }}
              transition={{ duration: 0.6, ease: "circOut" }}
              className="pointer-events-auto"
            >
              <GlitchHeader text={SHOWCASE_DATA[activeIdx].title} color={SHOWCASE_DATA[activeIdx].color} />
              
              <TerminalBox
                tag={SHOWCASE_DATA[activeIdx].tag}
                id={SHOWCASE_DATA[activeIdx].id}
                color={SHOWCASE_DATA[activeIdx].color}
              >
                <p className="font-mono text-sm leading-relaxed text-blue-100/85 mb-6">
                  {SHOWCASE_DATA[activeIdx].description}
                </p>

                <div className="grid grid-cols-1 gap-2 border-t border-white/5 pt-4">
                  {SHOWCASE_DATA[activeIdx].metrics.map((m, i) => (
                    <motion.div 
                      key={i} 
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <div className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: SHOWCASE_DATA[activeIdx].color }} />
                      <span className="font-mono text-[10px] text-white/75 tracking-widest">{m}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-6 flex items-center gap-2 text-[10px] font-bold tracking-[0.4em] uppercase" style={{ color: SHOWCASE_DATA[activeIdx].color }}>
                   <span className="w-2 h-2 rounded-full animate-pulse shadow-lg" style={{ 
                     backgroundColor: SHOWCASE_DATA[activeIdx].color,
                     boxShadow: `0 0 10px ${SHOWCASE_DATA[activeIdx].color}`
                   }} />
                   System_Active
                </div>
              </TerminalBox>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Vertical Progress Bar with white glow */}
        <div className="absolute right-10 top-1/2 -translate-y-1/2 flex flex-col gap-8 z-40 pointer-events-none">
            {SHOWCASE_DATA.map((item, i) => (
                <motion.div 
                  key={i} 
                  className={`w-1 h-12 rounded-full transition-colors duration-500 ${
                    i === activeIdx 
                      ? 'bg-white scale-x-150 shadow-[0_0_20px_white]' 
                      : 'bg-white/10'
                  }`}
                  animate={i === activeIdx ? { opacity: [0.8, 1, 0.8] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                />
            ))}
        </div>
      </div>
    </div>
  );
}
